import type { CodexItem } from '@reados/contracts';
type Value = Record<string, unknown>;
const text = (value: unknown) => typeof value === 'string' ? value : '';
const object = (value: unknown): Value => value && typeof value === 'object' && !Array.isArray(value) ? value as Value : {};
const workspaceImagePath = (value: unknown) => typeof value === 'string' && value.length <= 8192 && value.startsWith('/workspace/')
  && !/[\u0000-\u001f\u007f\\]/.test(value) && !value.slice(1).split('/').some(part => !part || part === '.' || part === '..') ? value : '';

/** Native items remain intact in ChatMessage.items; this is the iPad view DTO. */
export function nativeImagePreview(item: CodexItem): Value[] {
  const generated = item.type === 'imageGeneration';
  // Official app-server imageGeneration.savedPath is the output file identity.
  // `result` is opaque image data; prompts/URLs/other tool fields are not paths.
  const path = workspaceImagePath(generated ? item.savedPath : item.path), fileName = path.split('/').at(-1) || '图片';
  if (!path) return [];
  return [{ id: `${item.id}:image`, kind: 'file', title: fileName, filePath: path, fileName,
    attachmentKind: 'image', payload: { native_item_id: item.id,
      preview_kind: generated ? 'image_generation' : 'image_view', image_path: path,
      ...(generated ? { reados_native_image_generation: true } : { reados_native_image_view: true }) } }];
}

/** Stock add/delete diffs are the complete file contents, including whitespace. */
export function nativeChangeDiff(change: Value): string {
  const explicit = change.unified_diff ?? change.unifiedDiff;
  if (typeof explicit === 'string') return explicit;
  const content = text(change.diff), kind = text(object(change.kind).type) || text(change.kind);
  if (kind !== 'add' && kind !== 'delete') return content;
  const oldPath = text(change.path).replace(/^\/+/, ''), newPath = (text(object(change.kind).move_path ?? object(change.kind).movePath) || text(change.path)).replace(/^\/+/, '');
  if (!oldPath) return content;
  const lines = content === '' ? [] : content.split('\n');
  const trailingNewline = content.endsWith('\n');
  if (trailingNewline) lines.pop();
  const added = kind === 'add';
  const header = [`diff --git a/${oldPath} b/${newPath}`,
    added ? '--- /dev/null' : `--- a/${oldPath}`, added ? `+++ b/${newPath}` : '+++ /dev/null'];
  if (lines.length) header.push(added ? `@@ -0,0 +1,${lines.length} @@` : `@@ -1,${lines.length} +0,0 @@`,
    ...lines.map(line => `${added ? '+' : '-'}${line}`), ...(trailingNewline ? [] : ['\\ No newline at end of file']));
  return header.join('\n');
}

function diffStats(diff: string) {
  let added = 0, removed = 0, inHunk = false;
  for (const line of diff.split('\n')) {
    if (line.startsWith('diff --git ')) inHunk = false;
    else if (line.startsWith('@@')) inHunk = true;
    else if (inHunk && line.startsWith('+')) added++;
    else if (inHunk && line.startsWith('-')) removed++;
  }
  return { added, removed };
}

export function nativePatchPreview(item: CodexItem): Value[] {
  const changes = (Array.isArray(item.changes) ? item.changes : []).map(object).filter(change => text(change.path));
  if (!changes.length) return [];
  const normalized: Array<Value & { diff: string }> = changes.map(change => ({ ...change, kind: text(object(change.kind).type) || text(change.kind), diff: nativeChangeDiff(change),
    ...(text(change.move_path ?? change.movePath ?? object(change.kind).move_path ?? object(change.kind).movePath)
      ? { move_path: text(change.move_path ?? change.movePath ?? object(change.kind).move_path ?? object(change.kind).movePath) } : {}) }));
  const first = normalized[0], path = text(first.move_path) || text(first.path);
  const diff = normalized.map(change => change.diff).filter(Boolean).join('\n');
  const stats = diffStats(diff), fileName = normalized.length > 1 ? `${normalized.length} 个文件` : path.split('/').at(-1) || '文件';
  const nativeStatus = text(item.status);
  const patchStatus = nativeStatus === 'inProgress' || nativeStatus === 'running' || nativeStatus === 'streaming' ? 'editing'
    : nativeStatus === 'failed' ? 'failed' : nativeStatus === 'declined' ? 'declined' : 'applied';
  return [{ id: `${item.id}:patch`, kind: 'file', title: '文本文件修改', filePath: path, fileName, attachmentKind: 'text',
    payload: { call_id: item.id, patch_status: patchStatus, path, file_name: fileName,
      changes: normalized, render_diff: diff, turn_diff: diff, lines_added: stats.added, lines_removed: stats.removed,
      // Native Web has no undo/redo operation store. Do not invent operation_id.
      native_item: item } }];
}
